import CancelIcon from "@mui/icons-material/Cancel";
import { Box, IconButton, Stack, Typography } from "@mui/material";
import axios from "axios";
import { t } from "i18next";
import { useRef } from "react";
import { baseUrl } from "../../services";
import { handleOpenSnackBar } from "../../utils";

const useStyle = {
  iconButton: {
    position: "absolute",
    top: 0,
    right: 0,
  },

  addImageBox: {
    width: 130,
    height: 170,
    backgroundColor: "#f0f0f0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: 2,
    cursor: "pointer",
  },
};

const ProductImages = ({
  inputValues,
  setInputValues,
  setIsLoading,
  inputError,
  setInputError,
}) => {
  const imageRef = useRef(null);
  const handleChangeImage = (event) => {
    const file = event.target.files[0];

    const maxSize = 3 * 1024 * 1024;

    // Check file size
    if (file?.size > maxSize) {
      return handleOpenSnackBar("error", t("imageSizeLimit"));
    }

    const imageType = file?.type.split("/")[1].toLowerCase();

    const allowedFormats = ["png", "jpeg", "jpg"];
    const allowedVideoFormats = ["mp4", "mov", "avi", "mkv"]; // Not using now

    // Check if the image format is valid
    if (!allowedFormats.includes(imageType)) {
      return handleOpenSnackBar("error", t("allowedImageFormats"));
    }

    // If video file, show error
    if (allowedVideoFormats.includes(imageType)) {
      return handleOpenSnackBar("error", t("videoNotAllowed"));
    }

    // Use FileReader to load the image and check its dimensions
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        // Check if the image dimensions are at least 800x800
        if (img.width <= 999 || img.height <= 799) {
          return handleOpenSnackBar("error", t("minImageDimensions"));
        }

        // Proceed with the image upload
        const formData = new FormData();
        formData.append("image", file);
        setIsLoading(true);

        axios({
          method: "post",
          url: `${baseUrl}image/upload`,
          data: formData,
          headers: { "Content-Type": "multipart/form-data" },
        })
          .then((res) => {
            setIsLoading(false);
            imageRef.current.value = null;
            handleOpenSnackBar("success", t("imageUploadSuccess"));
            setInputError((prev) => ({ ...prev, images: false }));
            setInputValues((prev) => ({
              ...prev,
              images: [...prev.images, res?.data?.url],
            }));
          })
          .catch((error) => {
            setIsLoading(false);
            imageRef.current.value = null;
            handleOpenSnackBar("error", t("imageUploadFailed"));
          });
      };

      img.src = e.target.result; // Load image as source
    };

    reader.readAsDataURL(file); // Read the file as data URL
  };

  const handleRemoveImage = (index) => {
    let tempArray = [...inputValues.images];
    tempArray.splice(index, 1);
    setInputValues((prev) => ({ ...prev, images: tempArray }));
  };

  const renderAddImageBox = (index) => {
    if (inputValues?.images?.length < 4) {
      return (
        <Box onClick={() => imageRef.current.click()} sx={useStyle.addImageBox}>
          <Typography variant="h5" color="primary.main">
            +
          </Typography>
        </Box>
      );
    }
  };

  return (
    <>
      <Stack direction={"row"} flexWrap={"wrap"} gap={2}>
        {inputValues?.images?.map((img, index) => (
          <Box
            key={index}
            position={"relative"}
            width={130}
            height={170}
            borderRadius={2}
            sx={{
              background: `url(${img})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
            }}
          >
            <IconButton
              onClick={() => handleRemoveImage(index)}
              sx={useStyle.iconButton}
              size="small"
            >
              <CancelIcon htmlColor="red" fontSize="xs" />
            </IconButton>
          </Box>
        ))}
        {renderAddImageBox()}
      </Stack>
      {inputError?.images && (
        <Typography variant="caption" color="red" mt={1}>
          {t("uploadAtLeastTwoImages")}
        </Typography>
      )}
      <input
        type="file"
        accept="image/*"
        ref={imageRef}
        style={{ display: "none" }}
        onChange={handleChangeImage}
      />
    </>
  );
};

export default ProductImages;
